import { Button } from '@tempo-io/sui-button';
import { Add, Search } from '@tempo-io/sui-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useTranslation } from 'react-i18next';
import type { ItemFiltersType } from '@/common/api/types/itemFilters';
import type { ItemTypeMap } from '@/common/api/types/itemType';
import { ItemPicker } from '@/features/items/components/custom-field-value/ItemPicker';

interface AddDependencyControlProps {
  addLabel: string;
  pickerFilter: ItemFiltersType;
  excludedItemIds: number[];
  itemTypesById: ItemTypeMap;
  isAdding: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  eagerLoad: boolean;
  scenarioLoopId?: number;
  scenarioId?: number;
  onOpen: () => void;
  onCancel: () => void;
  onSelect: (pickedItemId: number) => void;
}

export const AddDependencyControl = ({
  addLabel,
  pickerFilter,
  excludedItemIds,
  itemTypesById,
  isAdding,
  isSubmitting,
  errorMessage,
  eagerLoad,
  scenarioLoopId,
  scenarioId,
  onOpen,
  onCancel,
  onSelect,
}: AddDependencyControlProps) => {
  const { t } = useTranslation();

  return (
    <Popover
      open={isAdding}
      onOpenChange={(next) => {
        if (next) {
          onOpen();
        } else if (!isSubmitting) {
          onCancel();
        }
      }}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="default" size="icon-sm" aria-label={addLabel} className="shrink-0">
                <Add />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{addLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        align="end"
        className="w-72 border-0 bg-transparent p-0 shadow-none"
        aria-label={addLabel}
      >
        {!!errorMessage && (
          <span
            role="alert"
            aria-live="polite"
            className="mb-1 block rounded-md border border-border-neutral bg-bg-surface-base p-2 text-xs text-text-error shadow-md"
            data-testid="add-dependency-error"
          >
            {errorMessage}
          </span>
        )}
        <ItemPicker
          value={null}
          onChange={(picked) => {
            if (picked) {
              onSelect(picked.id);
            }
          }}
          itemTypesById={itemTypesById}
          filter={pickerFilter}
          excludedItemIds={excludedItemIds}
          placeholder={t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.pickerPlaceholder)}
          autoFocus
          disabled={isSubmitting}
          isClearable={false}
          withExternalKey
          persistInput
          eagerLoad={eagerLoad}
          loopId={scenarioLoopId}
          scenarioId={scenarioId}
          leadingIcon={<Search className="size-6" />}
        />
      </PopoverContent>
    </Popover>
  );
};
